import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Divider,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as AuthActions from '../../Store/Actions/AuthActions/AuthActions';
import { validateEmail, validatePassword } from '../../Services/Validation';
import useStyles from './SigninStyle';
import RouteUrls from '../../Router/RouteUrls';
import { getTenantCode } from '../../Utils/tenantUtils';
import { getClientAssets } from '../../Config/clientAssets';
import { capitalizeFirstLetter } from '../../Services/UtilService/UtilService';
import { LOGIN_EMAIL_VERIFICATION_ERROR } from '../../constant/constants';

const FormBasedLogin = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);

    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    } else {
      // Check email domain based on tenant configuration
      const tenantCode = getTenantCode();
      const clientConfig = getClientAssets(tenantCode);
      const emailDomain = email.split('@')[1];

      if (emailDomain !== clientConfig.domain) {
        newErrors.email = `Email must be from the domain @${clientConfig.domain}`;
      }
    }

    if (!passwordValidation.isValid) {
      newErrors.password = passwordValidation.errors.join(' ');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setFormError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      await dispatch(
        AuthActions.login({
          email,
          password,
          provider: 'basic'
        })
      );
      setLoading(false);
      history.push(RouteUrls.rewardDashboard);
    } catch (error) {
      setLoading(false);
      let errorMessage = 'Sign in failed. Please try again.';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (error?.response?.data) {
        errorMessage = error.response.data.message || errorMessage;
      }
      errorMessage = capitalizeFirstLetter(errorMessage);
      if (errorMessage === LOGIN_EMAIL_VERIFICATION_ERROR) {
        history.push({
          pathname: RouteUrls.emailVerification,
          search: `?email=${encodeURIComponent(email)}&fromLogin=true`
        });
        return;
      }
      setFormError(errorMessage);

      const errorTimeout = setTimeout(() => {
        setFormError('');
      }, 8000);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  return (
    <div data-testid="form-based-login">
      {formError && (
        <Typography color="error" className={`${classes.formError} ${classes.fadeError}`}>
          {formError}
        </Typography>
      )}
      <form onSubmit={handleSubmit} className={classes.form} noValidate>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={e => setEmail(e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
        />

        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          error={!!errors.password}
          helperText={errors.password}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleTogglePasswordVisibility}
                  edge="end"
                  disabled={loading}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submitButton}
          disabled={loading}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </Button>

        <Divider className={classes.divider} />

        <Typography variant="body2" className={classes.signUpLink}>
          New User?{' '}
          <button
            type="button"
            onClick={() => history.push('/signup')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              color: 'blue',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            Sign Up
          </button>
        </Typography>

        <Typography
          variant="body2"
          className={classes.forgotPassword}
          style={{ textAlign: 'center', marginTop: 8, cursor: 'pointer' }}
        >
          <Button
            variant="text"
            size="small"
            color="primary"
            onClick={() => history.push(RouteUrls.forgotPassword)}
          >
            Forgot Password?
          </Button>
        </Typography>
      </form>
    </div>
  );
};

export default FormBasedLogin;
